import { z } from 'zod';

// Nominal Brand using a string-literal property name. We avoid `unique symbol`
// here because TypeScript cannot emit the symbol identifier into .d.ts files
// generated by sibling schemas (TS4023 "cannot be named"). The string-literal
// approach is portable and works for `tsc --declaration` across packages.
export type Brand<T, B extends string> = T & { readonly __brand: B };

export type MoneyCents = Brand<number, 'MoneyCents'>;
export type ScoreValue = Brand<number, 'ScoreValue'>;
export type Months = Brand<number, 'Months'>;
export type Hours = Brand<number, 'Hours'>;
export type Percentage = Brand<number, 'Percentage'>;

const intInRange = (min: number, max: number, name: string) =>
  z
    .number()
    .int(`${name} must be an integer`)
    .min(min, `${name} must be ≥ ${min}`)
    .max(max, `${name} must be ≤ ${max}`);

export const MoneyCentsSchema = z
  .number()
  .int('Money must be in integer cents (no floats)')
  .transform((v) => v as MoneyCents);

export const ScoreValueSchema = intInRange(0, 100, 'ScoreValue').transform((v) => v as ScoreValue);

export const MonthsSchema = z
  .number()
  .int()
  .nonnegative()
  .transform((v) => v as Months);

export const HoursSchema = z
  .number()
  .nonnegative()
  .transform((v) => v as Hours);

export const PercentageSchema = z
  .number()
  .min(0, 'Percentage must be ≥ 0')
  .max(100, 'Percentage must be ≤ 100')
  .transform((v) => v as Percentage);

export const eurosToCents = (euros: number): MoneyCents => Math.round(euros * 100) as MoneyCents;
export const centsToEuros = (cents: MoneyCents): number => (cents as number) / 100;
